// Saisie de terrain hors connexion (cadrage, section 9 ; architecture, section 8) pour les modules du lot 6 :
//   - brouillons d'inspection sérialisables conservés sur l'appareil, chiffrés (AES-GCM, clé de session) ;
//   - photos d'inspection prises sans réseau, conservées chiffrées sur l'appareil (cadrage, section 9) et
//     envoyées au serveur au retour du réseau, avant les réponses qui les citent ;
//   - paquet de travail de l'inspecteur conservé chiffré, pour ouvrir ses missions sans réseau ;
//   - opérations (constat d'inspection, réponses du formulaire, signalement d'incident) confiées au synchroniseur
//     commun (H3, `horsligne/`) : file unique de l'appareil, envoi par lots à /api/v1/sync, reprise automatique ;
//     rien n'est présenté comme reçu avant l'accusé du serveur.
// Ce module n'ajoute que ses magasins locaux (brouillons, paquet de l'inspecteur, photos) et la préparation des
// réponses avant envoi (photos d'abord).

import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { LIBELLES_TYPES_OPERATIONS } from '@carburanttrack/shared/logistique';
import { api, ErreurApi } from '../../api/client';
import { CoffreLocal, type Chiffre, type ChiffreBinaire } from '../../horsligne/chiffrement';
import { horsLigne } from '../../horsligne/contexte';
import { LIBELLES_ETATS_OPERATION, type EtatOperation, type FileHorsLigne, type OperationStockee } from '../../horsligne/file';
import { ErreurTransport, type BilanSynchro } from '../../horsligne/synchroniseur';
import { PREFIXE_PHOTO_LOCALE, estPhotoLocale, lireBrouillon, serialiserBrouillon, type Brouillon, type Reponses } from './modele';
import type { PaquetInspecteur } from './api';

export { LIBELLES_ETATS_OPERATION, LIBELLES_TYPES_OPERATIONS, type EtatOperation };

/** Photo conservée sur l'appareil : contenu chiffré, métadonnées en clair (nom, type, taille). */
export interface PhotoLocale {
  id: string;
  inspectionId: string;
  nom: string;
  type: string;
  taille: number;
  enregistreLe: string;
}

interface SchemaLocal extends DBSchema {
  brouillons: { key: string; value: { inspectionId: string; chiffre: Chiffre; enregistreLe: string } };
  paquets: { key: string; value: { cle: string; chiffre: Chiffre; enregistreLe: string } };
  photos: { key: string; value: PhotoLocale & { chiffre: ChiffreBinaire }; indexes: { inspectionId: string } };
}

/** Stockage de session indisponible (navigation privée stricte) : la saisie locale est alors désactivée. */
function stockageSession(): Storage | null {
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

function stockageLocal(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

let coffre: CoffreLocal | null = null;
let base: Promise<IDBPDatabase<SchemaLocal>> | null = null;

export function horsLigneDisponible(): boolean {
  return typeof indexedDB !== 'undefined' && stockageSession() !== null && stockageLocal() !== null && Boolean(globalThis.crypto?.subtle);
}

function coffreLocal(): CoffreLocal {
  coffre ??= new CoffreLocal(stockageSession()!);
  return coffre;
}

/** File de l'appareil : celle du synchroniseur commun (une seule file, un seul envoi). */
export function fileLocale(): FileHorsLigne {
  return horsLigne().file;
}

function baseLocale(): Promise<IDBPDatabase<SchemaLocal>> {
  base ??= openDB<SchemaLocal>('carburanttrack-terrain', 2, {
    upgrade(db, ancienne) {
      if (ancienne < 1) {
        db.createObjectStore('brouillons', { keyPath: 'inspectionId' });
        db.createObjectStore('paquets', { keyPath: 'cle' });
      }
      if (ancienne < 2) db.createObjectStore('photos', { keyPath: 'id' }).createIndex('inspectionId', 'inspectionId');
    },
  });
  return base;
}

// Brouillons -------------------------------------------------------------------------------------

export async function enregistrerBrouillonLocal(b: Brouillon): Promise<string> {
  const enregistreLe = new Date().toISOString();
  const chiffre = await coffreLocal().chiffrer(serialiserBrouillon(b));
  await (await baseLocale()).put('brouillons', { inspectionId: b.inspectionId, chiffre, enregistreLe });
  return enregistreLe;
}

/** Brouillon local de l'inspection, ou null (absent, ou illisible car la clé de session a été oubliée). */
export async function lireBrouillonLocal(inspectionId: string): Promise<{ brouillon: Brouillon; enregistreLe: string } | null> {
  const ligne = await (await baseLocale()).get('brouillons', inspectionId);
  if (!ligne) return null;
  try {
    return { brouillon: lireBrouillon(await coffreLocal().dechiffrer<string>(ligne.chiffre)), enregistreLe: ligne.enregistreLe };
  } catch {
    return null;
  }
}

/** Le brouillon a été reçu par le serveur (ou abandonné explicitement) : il n'a plus à rester sur l'appareil. */
export async function oublierBrouillonLocal(inspectionId: string): Promise<void> {
  await (await baseLocale()).delete('brouillons', inspectionId);
}

// Photos prises sans réseau ----------------------------------------------------------------------

function lireOctets(fichier: Blob): Promise<Uint8Array> {
  if (typeof fichier.arrayBuffer === 'function') return fichier.arrayBuffer().then((b) => new Uint8Array(b));
  return new Promise((resoudre, rejeter) => {
    const lecteur = new FileReader();
    lecteur.onerror = () => rejeter(lecteur.error ?? new Error('Lecture du fichier impossible'));
    lecteur.onload = () => resoudre(new Uint8Array(lecteur.result as ArrayBuffer));
    lecteur.readAsArrayBuffer(fichier);
  });
}

/** Conserve une photo chiffrée sur l'appareil ; renvoie son identifiant local, cité dans les réponses comme une photo. */
export async function conserverPhotoLocale(inspectionId: string, fichier: File): Promise<string> {
  const id = `${PREFIXE_PHOTO_LOCALE}${globalThis.crypto.randomUUID()}`;
  const chiffre = await coffreLocal().chiffrerOctets(await lireOctets(fichier));
  await (await baseLocale()).put('photos', { id, inspectionId, nom: fichier.name, type: fichier.type, taille: fichier.size, enregistreLe: new Date().toISOString(), chiffre });
  return id;
}

/** Photos conservées sur l'appareil pour une inspection (métadonnées seulement). */
export async function photosLocalesDe(inspectionId: string): Promise<PhotoLocale[]> {
  const lignes = await (await baseLocale()).getAllFromIndex('photos', 'inspectionId', inspectionId);
  return lignes.map(({ chiffre: _chiffre, ...p }) => p);
}

export async function oublierPhotoLocale(id: string): Promise<void> {
  await (await baseLocale()).delete('photos', id);
}

/**
 * Envoie au serveur une photo conservée sur l'appareil (chiffrée en transit par HTTPS, chiffrée au repos par
 * le serveur). L'identifiant local sert d'identifiant client : un envoi répété après un accusé perdu renvoie
 * le même document. Renvoie l'identifiant du document.
 */
async function televerserPhotoLocale(photo: PhotoLocale & { chiffre: ChiffreBinaire }): Promise<string> {
  const octets = await coffreLocal().dechiffrerOctets(photo.chiffre);
  const donnees = new FormData();
  donnees.append('identifiantClient', photo.id.slice(PREFIXE_PHOTO_LOCALE.length));
  donnees.append('fichier', new File([octets as BlobPart], photo.nom, { type: photo.type }));
  const r = await api<{ documentId: string }>(`/api/v1/inspections/${photo.inspectionId}/photos`, { formulaire: donnees });
  return r.donnees.documentId;
}

/**
 * Remplace, dans les réponses, chaque photo locale par le document créé sur le serveur, en envoyant les photos
 * au préalable. Une photo locale introuvable sur l'appareil (stockage purgé) est retirée des réponses et
 * signalée. Sans réseau, l'erreur d'acheminement est renvoyée telle quelle, rien n'est modifié.
 */
export async function resoudrePhotosLocales(inspectionId: string, reponses: Reponses): Promise<{ reponses: Reponses; envoyees: number; perdues: string[] }> {
  const citees = new Set<string>();
  for (const instances of Object.values(reponses)) for (const valeurs of Object.values(instances)) for (const v of Object.values(valeurs)) if (Array.isArray(v)) for (const x of v) if (typeof x === 'string' && estPhotoLocale(x)) citees.add(x);
  if (citees.size === 0) return { reponses, envoyees: 0, perdues: [] };
  const correspondance = new Map<string, string | null>();
  const db = await baseLocale();
  for (const id of citees) {
    const photo = await db.get('photos', id);
    if (!photo || photo.inspectionId !== inspectionId) {
      correspondance.set(id, null);
      continue;
    }
    correspondance.set(id, await televerserPhotoLocale(photo));
  }
  const resolues: Reponses = {};
  for (const [section, instances] of Object.entries(reponses)) {
    resolues[section] = {};
    for (const [instance, valeurs] of Object.entries(instances)) {
      resolues[section]![instance] = Object.fromEntries(
        Object.entries(valeurs).map(([champ, v]) => [champ, Array.isArray(v) ? v.map((x) => (correspondance.has(x) ? correspondance.get(x)! : x)).filter((x): x is string => x !== null) : v]),
      );
    }
  }
  for (const [id, documentId] of correspondance) if (documentId) await oublierPhotoLocale(id);
  return { reponses: resolues, envoyees: [...correspondance.values()].filter(Boolean).length, perdues: [...correspondance.entries()].filter(([, d]) => !d).map(([id]) => id) };
}

// Paquet de travail ------------------------------------------------------------------------------

export async function conserverPaquet(p: PaquetInspecteur): Promise<void> {
  const chiffre = await coffreLocal().chiffrer(p);
  await (await baseLocale()).put('paquets', { cle: 'inspecteur', chiffre, enregistreLe: new Date().toISOString() });
}

export async function lirePaquet(): Promise<{ paquet: PaquetInspecteur; enregistreLe: string } | null> {
  const ligne = await (await baseLocale()).get('paquets', 'inspecteur');
  if (!ligne) return null;
  try {
    return { paquet: await coffreLocal().dechiffrer<PaquetInspecteur>(ligne.chiffre), enregistreLe: ligne.enregistreLe };
  } catch {
    return null;
  }
}

// File d'opérations et envoi (synchroniseur commun) ----------------------------------------------

/** Vrai pour une erreur d'acheminement (pas de réseau, serveur injoignable) : l'opération peut attendre. */
export function estErreurReseau(e: unknown): boolean {
  return e instanceof ErreurApi && e.statut === 0;
}

/**
 * Préparation des réponses d'inspection avant envoi : les photos prises sans réseau partent d'abord (envoi
 * multipart idempotent), les réponses envoyées citent alors les documents créés. Sans réseau, l'envoi est
 * interrompu et rien n'est modifié ; un refus du serveur sur une photo refuse l'opération avec son motif.
 */
async function preparerReponses(charge: unknown): Promise<unknown> {
  const c = charge as { inspectionId: string; reponses: Reponses };
  try {
    return { ...c, reponses: (await resoudrePhotosLocales(c.inspectionId, c.reponses)).reponses };
  } catch (e) {
    if (estErreurReseau(e)) throw new ErreurTransport(e instanceof Error ? e.message : 'Serveur injoignable');
    if (e instanceof ErreurApi && e.statut === 401) throw new ErreurTransport('Session expirée : reconnectez-vous pour envoyer', true);
    throw new Error(e instanceof Error ? `Photo refusée par le serveur : ${e.message}` : 'Photo refusée par le serveur');
  }
}

let preparationDeclaree = false;

/** Synchroniseur commun, avec la préparation des réponses d'inspection déclarée une fois. */
function synchroniseurCommun() {
  const { synchroniseur } = horsLigne();
  if (!preparationDeclaree) {
    synchroniseur.preparer('reponses_inspection', preparerReponses);
    preparationDeclaree = true;
  }
  return synchroniseur;
}

/**
 * Met une opération en file sur l'appareil (« enregistré sur cet appareil ») ; si le réseau est présent, elle
 * passe « en attente d'envoi » et l'envoi est lancé sans être attendu.
 */
export async function mettreEnFile(type: string, charge: unknown, versionObjet: string | null = null, horodatageEvenement = new Date()): Promise<OperationStockee> {
  return synchroniseurCommun().enregistrer(type, charge, { versionObjet, horodatageEvenement, resume: LIBELLES_TYPES_OPERATIONS[type] ?? null });
}

export async function operationsLocales(): Promise<OperationStockee[]> {
  return fileLocale().lister();
}

export type BilanSynchronisation = BilanSynchro;

/**
 * Envoi par lots des opérations en attente, par le synchroniseur commun. Chaque opération passe « envoyée, en
 * attente d'accusé » avant l'appel ; seul l'accusé du serveur la fait passer « synchronisée », « refusée »
 * (motif) ou « conflit ». Sans réseau, les opérations reviennent « en attente d'envoi » et le bilan porte
 * le motif de l'interruption.
 */
export async function synchroniser(): Promise<BilanSynchronisation> {
  return synchroniseurCommun().synchroniser();
}

/**
 * Opérations de l'appareil qui portent sur une inspection (constats, réponses), avec leur charge déchiffrée :
 * la fiche les affiche avec leur état et bloque une nouvelle saisie des réponses tant qu'un envoi est en cours.
 */
export async function operationsDeInspection(inspectionId: string): Promise<{ operation: OperationStockee; charge: Record<string, unknown> }[]> {
  const f = fileLocale();
  const resultat = [];
  for (const o of await f.lister()) {
    if (o.type !== 'constat_inspection' && o.type !== 'reponses_inspection') continue;
    try {
      const charge = await f.charge<Record<string, unknown>>(o.identifiantClient);
      if (charge.inspectionId === inspectionId) resultat.push({ operation: o, charge });
    } catch {
      // Charge illisible (clé de session oubliée) : l'opération reste listée dans le bloc de synchronisation.
    }
  }
  return resultat;
}

const A_ENVOYER: EtatOperation[] = ['enregistre_local', 'en_attente_envoi', 'envoye'];
export const ETATS_EN_ATTENTE: EtatOperation[] = A_ENVOYER;

/** Contrôle d'intégrité de la file (purge du stockage par le système, cadrage section 9). */
export async function verifierFile(): Promise<{ intacte: boolean; perdus: string[] }> {
  return fileLocale().verifierIntegrite();
}
